import { PAYMENT_STATUS, PAYMENT_TYPES, STRIPE_DISPUTE_STATUS } from '@/lib/constants';
import { PaymentOrder, PaymentType } from '@/lib/api/income/types';
import { isStatusDisputed, isStatusPaid } from '../income.logic';
import { fetchOrders } from '@/lib/api/income/api';
import { downloadCSV } from '@/lib';
import { MAX_ORDERS_PER_PAGE } from '../constants/transactionConstants';
import { formatDistanceToNowStrict } from 'date-fns';

export function isOrderStatusFailed(order: PaymentOrder): boolean {
    return order.status === PAYMENT_STATUS.FAILED && !order.data.retrying;
}

export function isOrderStatusRetrying(order: PaymentOrder): boolean {
    return order.status === PAYMENT_STATUS.PENDING && !!order.data.retrying;
}

export function isOrderStatusInitialized(order: PaymentOrder): boolean {
    return order.status === PAYMENT_STATUS.PAYMENT_INITIALISED;
}

export function isOrderStatusPending(order: PaymentOrder): boolean {
    return order.status === PAYMENT_STATUS.PENDING;
}

export function isOrderStatusRefunded(order: PaymentOrder): boolean {
    return order.status === PAYMENT_STATUS.REFUNDED;
}

export function isOrderDisputeWon(order: PaymentOrder): boolean {
    return !!(order.data?.dispute_id && order.data?.dispute_status === STRIPE_DISPUTE_STATUS.WON);
}
export function isOrderDisputedTransactionWon(order: PaymentOrder): boolean {
    return !!(order.data?.dispute_status === STRIPE_DISPUTE_STATUS.WON);
}

export function getOrderStatus(order: PaymentOrder) {
    if (order.type === PAYMENT_TYPES.ACCOUNT_CHARGE) {
        return { 'text-gray': true };
    }
    const isDisputed = isStatusDisputed(order.status);
    return {
        'text-success-moderate': isStatusPaid(order.status) || isDisputed,
        'text-gray': isOrderStatusRefunded(order),
        'text-secondary': isOrderStatusInitialized(order) || isOrderStatusPending(order),
        'text-negative-active':
            (isDisputed && !isOrderDisputeWon(order)) || isOrderStatusFailed(order) || isOrderStatusRetrying(order),
    };
}

// Tag helper functions
export function isPaymentPlan(order: PaymentOrder): boolean {
    return !!order.data.payment_plan_id;
}

export function getPaymentPlanLabel(order: PaymentOrder): string {
    if (order.data.payments_captured && order.data.total_payments) {
        return `${order.data.payments_captured} / ${order.data.total_payments} Payments`;
    }
    return 'Payment Plans';
}

export function isPayoutOrder(order: PaymentOrder): boolean {
    return order.type === PAYMENT_TYPES.PAYOUT;
}

export function getPayoutStatusLabel(order: PaymentOrder): string {
    const estimatedArrivalDate = order.pos_data?.payout_details?.arrival_date
        ? order.pos_data.payout_details.arrival_date * 1000
        : null;

    // Over one day out
    if (estimatedArrivalDate && estimatedArrivalDate > Date.now() + 86400000) {
        return `Est ${formatDistanceToNowStrict(estimatedArrivalDate, { addSuffix: false })}`;
    }
    if (estimatedArrivalDate && estimatedArrivalDate > Date.now()) {
        return 'Today';
    }
    return 'In Transit';
}

export function isAffiliateTransaction(order: PaymentOrder): boolean {
    return (
        !!order.data?.affiliate_share?.is_qualified_affiliate_transaction && order.type !== PAYMENT_TYPES.AFFILIATE_FEE
    );
}

export function isOrderBump(order: PaymentOrder): boolean {
    return !!(order.data?.order_bump && order.data?.order_bump_parent_page);
}

// Fulfillment helper
export function isOrderNeedsFulfillment(order: PaymentOrder): boolean {
    return (
        [PaymentType.FULFILLMENT, PaymentType.DIGITAL_DOWNLOAD].includes(order.type) &&
        order.status === PAYMENT_STATUS.PAID_NOT_FULFILLED
    );
}

// Payment info tooltip
export function getOrderPaymentInfo(order: PaymentOrder): string {
    let paymentStatus = 'Unknown';

    if (isStatusPaid(order.status)) {
        paymentStatus = 'complete';
    } else if (isOrderStatusRefunded(order)) {
        paymentStatus = 'The payment was refunded';
        if (order.refunded_on) {
            paymentStatus += ` on ${order.refunded_on}`;
        }
        return `${paymentStatus}.`;
    } else if (isOrderStatusFailed(order)) {
        return 'The payment has failed';
    } else if (isOrderStatusRetrying(order)) {
        paymentStatus = 'retrying';
    } else if (isStatusDisputed(order.status)) {
        paymentStatus = 'disputed';
    } else if (isOrderStatusPending(order)) {
        paymentStatus = 'pending';
    }

    return `The payment is ${paymentStatus}`;
}

// Helper function to prepare question answers for CSV export
export function prepareQuestionAnswersForCSVExport(
    questionsAndAnswers?: Array<{ question: string; answer: string | string[] }>
): { csvColumns: Record<string, { callback: (data: any) => string }>; data: Record<string, string> } {
    if (!questionsAndAnswers) {
        return { csvColumns: {}, data: {} };
    }

    const csvColumns: Record<string, { callback: (data: any) => string }> = {};
    const data: Record<string, string> = {};

    questionsAndAnswers.forEach(qa => {
        if (qa.answer !== undefined && qa.answer !== '') {
            // Expand any csv values so they do not affect CSV
            let answerValUse = qa.answer;
            if (typeof qa.answer === 'string' || qa.answer instanceof String) {
                answerValUse = qa.answer.split(',').length > 1 ? qa.answer.split(',') : answerValUse;
            }

            // Set csvColumn values
            csvColumns[qa.question] = {
                callback: (data: any) => data[qa.question],
            };

            // Set data values
            if (Array.isArray(answerValUse)) {
                // To represent CSV values in csv wrap in quotes
                data[qa.question] = `"${answerValUse.join(',')}"`;
            } else {
                data[qa.question] = String(answerValUse);
            }
        }
    });

    return { csvColumns, data };
}

export const ordersToCSV = (
    orders: PaymentOrder[],
    dynamicHeaders: string[] = [],
    getDynamicRowData?: (order: PaymentOrder) => Record<string, string>
) => {
    const getPaymentPlanDetails = (order: PaymentOrder) => {
        return order.data.payments_captured && order.data.total_payments
            ? `${order.data.payments_captured} / ${order.data.total_payments} Payments`
            : '';
    };

    const headers = [
        'Date & Time',
        'Full Name',
        'Email',
        'Product',
        'Type',
        'Receiving Currency',
        'Amount',
        'Tax Amount',
        'Converted Currency',
        'Conversion',
        'Processing Fees',
        'Net Revenue',
        'Refunded',
        'Paid',
        'Payment Method',
        'Credit Card Issue Country',
        'Tax Reason',
        'Tax Type',
        'Tax Rate',
        'Billing Address',
        'Affiliate Username',
        'Payment Plan Details',
        ...dynamicHeaders,
    ];

    const rows = orders.map(order => {
        const baseRow = [
            order.created_at,
            order.full_name,
            order.email || '',
            order.data.title || '',
            order.type === PAYMENT_TYPES.PAYOUT ? 'Cash Out' : 'Transaction',
            order.pos_currency,
            order.amount,
            order.tax,
            order.settlement_currency,
            order.conversion,
            order.fee,
            order.net,
            order.status === PAYMENT_STATUS.REFUNDED ? 'Yes' : 'No',
            isStatusPaid(order.status) ? 'Yes' : 'No',
            order.payment_gateway,
            order.cc_issue_country,
            order.taxability_reason,
            order.tax_type,
            `${order.tax_rate}${order.tax_rate ? '%' : ''}`,
            order.billing_address ?? '',
            order.affiliated_username ?? '',
            getPaymentPlanDetails(order),
        ];

        // Add dynamic fields
        if (getDynamicRowData) {
            const dynamicData = getDynamicRowData(order);
            dynamicHeaders.forEach(header => {
                baseRow.push(dynamicData[header] || '');
            });
        }

        return baseRow;
    });

    const today = new Date();
    const filename = `Your Stan Transactions - ${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}.csv`;
    downloadCSV({
        filename,
        headers,
        rows,
    });
};

export const exportOrdersToCSV = async (
    backendFilterValues?: string,
    userFanId?: number,
    onProgress?: (pagesFetched: number, totalPages: number) => void
) => {
    // First, fetch page 1 to determine total pages
    const firstPage = await fetchOrders({
        per_page: 1000,
        page: 1,
        filter_array: backendFilterValues,
        user_fan_id: userFanId,
    });
    const { last_page } = firstPage;

    // Calculate total export pages (Vue uses totalExportPages which is total pages to fetch)
    const totalExportPages = last_page;
    let pagesFetched = 1; // We've fetched page 1
    onProgress?.(pagesFetched, totalExportPages);

    // Collect all dynamic headers from all orders
    const allDynamicHeaders = new Set<string>();
    const allOrders: PaymentOrder[] = [...firstPage.data];

    // Process first page for dynamic headers
    firstPage.data.forEach(order => {
        if (order.data?.additional_information) {
            const { csvColumns } = prepareQuestionAnswersForCSVExport(order.data.additional_information);
            Object.keys(csvColumns).forEach(header => allDynamicHeaders.add(header));
        }
    });

    // If only one page, use that data
    if (last_page === 1) {
        const dynamicHeadersArray = Array.from(allDynamicHeaders);
        const getDynamicRowData = (order: PaymentOrder) => {
            if (order.data?.additional_information) {
                const { data } = prepareQuestionAnswersForCSVExport(order.data.additional_information);
                return data;
            }
            return {};
        };
        ordersToCSV(firstPage.data, dynamicHeadersArray, getDynamicRowData);
        return;
    }

    // Fetch all pages sequentially to show progress
    for (let page = 2; page <= last_page; page++) {
        const pageResult = await fetchOrders({
            per_page: MAX_ORDERS_PER_PAGE,
            page,
            filter_array: backendFilterValues,
            user_fan_id: userFanId,
        });

        allOrders.push(...pageResult.data);
        pagesFetched++;

        // Collect dynamic headers from this page
        pageResult.data.forEach(order => {
            if (order.data?.additional_information) {
                const { csvColumns } = prepareQuestionAnswersForCSVExport(order.data.additional_information);
                Object.keys(csvColumns).forEach(header => allDynamicHeaders.add(header));
            }
        });

        onProgress?.(pagesFetched, totalExportPages);
    }

    // Process all orders with dynamic fields
    const dynamicHeadersArray = Array.from(allDynamicHeaders);
    const getDynamicRowData = (order: PaymentOrder) => {
        if (order.data?.additional_information) {
            const { data } = prepareQuestionAnswersForCSVExport(order.data.additional_information);
            return data;
        }
        return {};
    };

    ordersToCSV(allOrders, dynamicHeadersArray, getDynamicRowData);
};
